import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { staticImg, fmtMoney } from '../utils/media';
import { ArrowRight, Tag, Star, ShoppingCart } from 'lucide-react';

const badgeText = (p, tr) => (p.is_discounted ? tr('badge.discount', 'İNDİRİM') : p.is_new ? tr('badge.new', 'YENİ') : p.is_featured ? tr('badge.popular', 'POPÜLER') : null);
const badgeStyle = (b, tr) => (b === tr('badge.new', 'YENİ') ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : b === tr('badge.popular', 'POPÜLER') ? 'bg-blue-500/15 border-blue-500/40 text-blue-300' : 'bg-[#e4b44e]/15 border-[#e4b44e]/40 text-[#f4d896]');

export default function FeaturedProducts() {
  const { lang, tr } = useI18n();
  const { add } = useCart();
  const { toast } = useToast();
  const nav = useNavigate();
  const { data } = useApi(() => publicApi.products(lang, undefined, true), [lang]);
  const list = (data || []).slice(0, 4);
  if (list.length === 0) return null;

  const buy = (p) => (e) => {
    e.preventDefault(); e.stopPropagation();
    add(p, 1);
    toast({ title: tr('cart.added', 'Sepete eklendi ✓'), description: p.name });
    nav('/cart');
  };

  return (
    <section id="featured" className="relative py-16 md:py-24 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.3em] text-[#e4b44e]/80 mb-3">
              <Star className="w-3.5 h-3.5" /> {tr('home.featured', 'ÖNE ÇIKAN ÜRÜNLER')}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
              {tr('home.featured_title_1', 'Efsanevi')} <span className="gold-text">{tr('home.featured_title_2', 'Koleksiyon')}</span>
            </h2>
            <p className="text-white/60 mt-3 max-w-xl">{tr('home.featured_subtitle', 'Metin2 sunucuları için en çok tercih edilen premium paketler.')}</p>
          </div>
          <Link to="/products" className="btn-outline-gold inline-flex items-center gap-2 h-11 px-5 rounded-full text-xs font-extrabold tracking-widest w-fit">
            {tr('home.view_all', 'TÜMÜNÜ GÖR')} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {list.map((p) => {
            const badge = badgeText(p, tr);
            return (
              <article key={p.id} className="group relative rounded-2xl overflow-hidden border border-white/8 bg-gradient-to-b from-[#111015] to-[#0a090c] card-hover">
                <Link to={`/products/${p.slug}`} className="block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#0f0e12]">
                    <img src={staticImg(p.image)} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    {badge && <div className={`absolute top-4 left-4 px-2.5 py-1 text-[10px] font-black tracking-widest border rounded ${badgeStyle(badge, tr)}`}>{badge}</div>}
                    <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/15 flex items-center justify-center text-white/70"><Tag className="w-3.5 h-3.5" /></div>
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-bold tracking-[0.3em] text-[#e4b44e]/80 mb-2">{(p.category_name || '').toUpperCase()}</div>
                    <h3 className="font-display text-lg font-bold text-white leading-snug min-h-[3.2rem]">{p.name}</h3>
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        {p.old_price ? <div className="text-xs text-white/40 line-through">{fmtMoney(p.old_price)} ₺</div> : null}
                        <div className="font-display text-2xl font-black gold-text">{fmtMoney(p.price)} ₺</div>
                      </div>
                      <button onClick={buy(p)} title="Sepete ekle" className="w-10 h-10 rounded-full btn-gold flex items-center justify-center"><ShoppingCart className="w-4 h-4" /></button>
                    </div>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
